// src/components/Avatar/Avatar.tsx
import React from 'react';
import styles from './Avatar.module.css';

// 1. Adicionamos a prop 'onClick' à nossa interface
interface AvatarProps {
  name: string;
  picture?: string; // URL da imagem de perfil
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLDivElement>; // Tipo para evento de clique em uma div
}

export function Avatar({ name, picture, className, style, onClick }: AvatarProps) {
  const [showImage, setShowImage] = React.useState(false);
  const [currentPicture, setCurrentPicture] = React.useState<string | undefined>(picture);
  const [isLoading, setIsLoading] = React.useState(false);

  const getInitials = (name: string) => {
    const names = name.split(' ');
    const firstNameInitial = names[0] ? names[0][0] : '';
    const lastNameInitial = names.length > 1 ? names[names.length - 1][0] : '';
    return `${firstNameInitial}${lastNameInitial}`.toUpperCase();
  };

  const initials = getInitials(name);
  const combinedClassName = `${styles.avatar} ${className || ''}`;

  // Atualiza currentPicture apenas quando picture muda
  React.useEffect(() => {
    if (picture !== currentPicture) {
      setCurrentPicture(picture);
      setShowImage(false);
      setIsLoading(false);
    }
  }, [picture, currentPicture]);

  const handleImageLoad = () => {
    setShowImage(true);
    setIsLoading(false);
  };

  const handleImageError = () => {
    setShowImage(false);
    setIsLoading(false);
  };

  const handleImageStart = () => {
    setIsLoading(true);
  };

  const hasPicture = currentPicture && currentPicture.trim() !== '';

  return (
    <div className={combinedClassName} style={style} onClick={onClick}>
      {/* Se tem imagem e carregou, mostra só a imagem */}
      {showImage && hasPicture ? (
        <img 
          src={currentPicture} 
          alt={name}
          className={styles.avatarImage}
          style={{ 
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '50%'
          }}
        />
      ) : (
        /* Senão, mostra iniciais */
        <span className={styles.initials}>
          {initials}
        </span>
      )}
      
      {/* Imagem invisível para carregar */}
      {hasPicture && !showImage && !isLoading && (
        <img 
          src={currentPicture} 
          alt={name}
          onLoadStart={handleImageStart}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{ 
            position: 'absolute',
            opacity: 0,
            pointerEvents: 'none',
            width: '1px',
            height: '1px'
          }}
        />
      )}
    </div>
  );
}